/**
 * Sprint 24.15 — agent profile provenance footer (Sprint 23.6 extension).
 *
 * Renders ONLY when an agent was hired from a template
 * (agent_config.hired_from_template). Shows "Hired from <name> v<version>" with a
 * "view template" link that opens a read-only preview modal. A small footer
 * addition (Decision 13) — no structural redesign.
 */

import { useState, type CSSProperties } from "react";
import type { AgentConfig, LibraryNodeDetail } from "../../../lib/types";
import { usePreviewAgentTemplate } from "../../hooks/use-agent-templates";

export interface AgentProvenanceFooterProps {
  node: LibraryNodeDetail;
}

export function AgentProvenanceFooter({ node }: AgentProvenanceFooterProps) {
  const config = (node.agent_config ?? null) as AgentConfig | null;
  const provenance = config?.hired_from_template ?? null;
  const [open, setOpen] = useState(false);

  // Hook order must be stable — fetch is gated on a real id, so it's inert when
  // there's no provenance.
  const { data: template } = usePreviewAgentTemplate(provenance?.template_id ?? null);

  if (!provenance) return null;

  const name = template?.name ?? provenance.template_id;

  return (
    <>
      <div style={footer}>
        <i className="ti ti-template" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
        <span style={text}>
          Hired from {name} v{provenance.template_version}
        </span>
        <button type="button" onClick={() => setOpen(true)} style={link}>
          view template
        </button>
      </div>

      {open && template && (
        <div style={modalBackdrop} role="dialog" aria-modal="true" aria-label={`${template.name} template`} onClick={() => setOpen(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)" }}>
                {template.name} <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}>v{template.version}</span>
              </span>
              <button type="button" onClick={() => setOpen(false)} style={closeBtn} aria-label="Close">
                <i className="ti ti-x" style={{ fontSize: 15 }} aria-hidden="true" />
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--color-text-primary)" }}>{template.persona}</p>
            <div style={instructionsPanel}>{template.instructions}</div>
            {template.default_equipment.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {template.default_equipment.map((e) => (
                  <span key={e.kind} style={equipChip}>
                    {e.kind}
                    {e.required ? " ·  required" : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 16px",
  marginTop: 8,
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
};
const text: CSSProperties = { fontSize: 12, color: "var(--color-text-secondary)" };
const link: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const modalBackdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 300,
};
const modalCard: CSSProperties = {
  width: 520,
  maxWidth: "92vw",
  maxHeight: "82vh",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 20,
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  boxShadow: "0 24px 64px rgba(0, 0, 0, 0.32)",
};
const closeBtn: CSSProperties = {
  width: 28,
  height: 28,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: "var(--border-radius-sm)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const instructionsPanel: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "12px 14px",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  lineHeight: 1.65,
  color: "var(--color-text-primary)",
  whiteSpace: "pre-wrap",
};
const equipChip: CSSProperties = {
  fontSize: 11,
  padding: "2px 8px",
  borderRadius: 999,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
};
